import React, { useEffect, useState, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import { listTransactions } from '../graphql/queries';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { formatCurrency } from '../utils/formatters';

const client = generateClient({
  authMode: 'userPool'
});

interface Transaction {
  id: string;
  type: 'EXPENSE' | 'INCOME';
  amount: number;
  categoryID?: string;
  category?: {
    id: string;
    name: string;
    description?: string;
  } | string; // Can be string for backward compatibility
  description?: string;
  date: string;
  createdByID: string;
  createdBy?: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
  userInitials?: string; // For backward compatibility
}

interface ListTransactionsQuery {
  listTransactions: {
    items: Transaction[];
    nextToken?: string;
  };
}

interface TransactionModalProps {
  transaction: Transaction;
  onClose: () => void;
}

interface ReportProps {
  // Remove familyId prop
}

const TransactionModal: React.FC<TransactionModalProps> = ({ transaction, onClose }) => {
  return (
    <div className="transaction-modal" onClick={onClose}>
      <div className="transaction-modal-content" onClick={e => e.stopPropagation()}>
        <div className="transaction-modal-header">
          <span className="transaction-modal-date">
            {new Date(transaction.date).toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
          <span className={`transaction-modal-type ${transaction.type === 'EXPENSE' ? 'expense' : 'income'}`}>
            {transaction.type === 'EXPENSE' ? 'Gasto' : 'Ingreso'}
          </span>
        </div>
        <div className="transaction-modal-details">
          <div className={`transaction-modal-amount ${transaction.type === 'EXPENSE' ? 'expense' : 'income'}`}>
            {formatCurrency(transaction.amount)}
          </div>
          <div className="transaction-modal-description">
            {transaction.description || '-'}
          </div>
        </div>
        <div className="transaction-modal-info">
          <div className="transaction-modal-field">
            <span className="transaction-modal-label">Categoría</span>
            <span className="transaction-modal-value">
              {typeof transaction.category === 'string' 
                ? transaction.category 
                : transaction.category?.name || 'Sin categoría'}
            </span>
          </div>
          <div className="transaction-modal-field">
            <span className="transaction-modal-label">Usuario</span>
            <span className="transaction-modal-value">
              {transaction.createdBy?.name || transaction.userInitials}
            </span>
          </div>
        </div>
        <button className="transaction-modal-close" onClick={onClose}>
          Cerrar
        </button>
      </div>
    </div>
  );
};

const Report: React.FC<ReportProps> = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const reportRef = useRef<HTMLDivElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await client.graphql({
        query: listTransactions,
        variables: {
          limit: 100,
          filter: {
            createdByID: {
              ne: null
            }
          }
        }
      }) as GraphQLResult<ListTransactionsQuery>;
      
      if (!result.data) {
        throw new Error('No data received from API');
      }
      
      const items = result.data.listTransactions.items;
      if (!Array.isArray(items)) {
        throw new Error('Invalid response format');
      }
      
      // Process items to ensure they have all the necessary properties
      const processedItems = items.map(transaction => {
        // Map user ID to actual user name based on their ID
        let userName = 'Usuario';
        
        // Match the transaction's createdByID to a known user
        if (transaction.createdByID === '84088418-c091-7066-1a51-5bb81b6b4010') {
          userName = 'User One';
        } else if (transaction.createdByID === '64d8d4f8-e061-705c-5e39-b9d69649090f') {
          userName = 'User Two';
        } else if (transaction.createdBy && transaction.createdBy.name) {
          userName = transaction.createdBy.name;
        }
        
        // Create user initials from name
        const userInitials = userName.split(' ')
          .map(name => name[0])
          .join('')
          .toUpperCase();
        
        // Add backward compatibility fields
        return {
          ...transaction,
          userInitials,
          // Ensure category is a string for backward compatibility
          category: typeof transaction.category === 'string' 
            ? transaction.category 
            : transaction.category?.name || 'Sin categoría'
        };
      });
      
      setTransactions(processedItems);
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      const errorMessage = error.message || 'Error al cargar las transacciones';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const filteredTransactions = transactions
    .filter(transaction => {
      // First filter by month/year
      const transactionDate = new Date(transaction.date);
      const isInSelectedMonth = 
        transactionDate.getMonth() === currentDate.getMonth() &&
        transactionDate.getFullYear() === currentDate.getFullYear();

      // Then filter by search term if one exists
      if (!searchTerm) return isInSelectedMonth;

      const searchLower = searchTerm.toLowerCase();
      const dateStr = new Date(transaction.date).toLocaleDateString('es-ES');
      const amountStr = transaction.amount.toString();

      return isInSelectedMonth && (
        dateStr.includes(searchLower) ||
        (transaction.description || '').toLowerCase().includes(searchLower) ||
        amountStr.includes(searchLower)
      );
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const totals = filteredTransactions.reduce(
    (acc, transaction) => {
      if (transaction.type === 'EXPENSE') {
        acc.expenses += transaction.amount;
      } else {
        acc.income += transaction.amount;
      }
      return acc;
    },
    { expenses: 0, income: 0 }
  );

  const balance = totals.income - totals.expenses;

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
  };

  const formatMonth = (date: Date) => {
    const month = date.toLocaleString('es-ES', { month: 'long' });
    const year = date.getFullYear();
    return `${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleGeneratePDF = async () => {
    try {
      // Create new document with proper initialization
      const doc = new jsPDF('p', 'mm', 'a4');

      // Add title
      doc.setFontSize(20);
      doc.text('Reporte Mensual', 105, 20, { align: 'center' });
      
      // Add month
      doc.setFontSize(14);
      doc.text(formatMonth(currentDate), 105, 30, { align: 'center' });

      // Add summary section with proper positioning
      doc.setFontSize(12);
      doc.text(`Total Ingresos: $${totals.income.toFixed(2)}`, 20, 50);
      doc.text(`Total Gastos: $${totals.expenses.toFixed(2)}`, 20, 60);
      doc.text(`Balance: $${balance.toFixed(2)}`, 20, 70);

      // Prepare table data
      const tableData = filteredTransactions.map(t => [
        new Date(t.date).toLocaleDateString('es-ES'),
        t.description || '-',
        typeof t.category === 'string' 
          ? t.category 
          : t.category?.name || 'Sin categoría',
        t.type === 'EXPENSE' ? `-$${t.amount.toFixed(2)}` : `$${t.amount.toFixed(2)}`,
        t.userInitials || 'Usuario'
      ]);

      // Add table with proper configuration
      doc.autoTable({
        startY: 90,
        head: [['Fecha', 'Descripción', 'Categoría', 'Monto', 'Usuario']],
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 10,
          cellPadding: 5
        },
        headStyles: {
          fillColor: [10, 7, 58],
          textColor: [255, 255, 255]
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { top: 90 }
      });

      // Save the PDF with proper name
      doc.save(`reporte-${formatMonth(currentDate).toLowerCase()}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error al generar el PDF. Por favor, intente nuevamente.');
    }
  };

  if (loading) {
    return <div>Cargando reporte...</div>;
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={fetchTransactions}>Intentar nuevamente</button>
      </div>
    );
  }

  return (
    <div className="report-container">
      <div className="report-controls">
        <div className="month-navigation">
          <button onClick={handlePreviousMonth}>←</button>
          <span className="current-month">{formatMonth(currentDate)}</span>
          <button onClick={handleNextMonth}>→</button>
        </div>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por fecha, descripción o monto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="report-actions">
          <button onClick={handlePrint} className="print-button">
            Imprimir Reporte
          </button>
          <button onClick={handleGeneratePDF} className="pdf-button">
            Exportar PDF
          </button>
        </div>
      </div>

      <div ref={reportRef} className="report-content screen-only">
        <div className="totals-cards">
          <div className="total-card income">
            <div className="total-card-content">
              <span className="total-label">Total Ingresos</span>
              <span className="total-amount">${totals.income.toFixed(2)}</span>
            </div>
          </div>
          <div className="total-card expenses">
            <div className="total-card-content">
              <span className="total-label">Total Gastos</span>
              <span className="total-amount">${totals.expenses.toFixed(2)}</span>
            </div>
          </div>
          <div className="total-card balance">
            <div className="total-card-content">
              <span className="total-label">Balance</span>
              <span className={`total-amount ${balance >= 0 ? 'positive' : 'negative'}`}>
                ${balance.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="mobile-transactions">
          {filteredTransactions.map((transaction) => (
            <div 
              key={transaction.id} 
              className="report-transaction-card"
              onClick={() => setSelectedTransaction(transaction)}
            >
              <div className="report-transaction-user">
                {transaction.userInitials || 'Usuario'}
              </div>
              <div className="report-transaction-content">
                <div className="report-transaction-description">
                  {transaction.description || '-'}
                </div>
                <div className="report-transaction-date">
                  {new Date(transaction.date).toLocaleDateString('es-ES', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}
                </div>
              </div>
              <div className="report-transaction-right">
                <span className={`report-transaction-amount ${transaction.type === 'EXPENSE' ? 'expense' : 'income'}`}>
                  {formatCurrency(transaction.amount)}
                </span>
                <span className={`report-transaction-type ${transaction.type === 'EXPENSE' ? 'expense' : 'income'}`}>
                  {transaction.type === 'EXPENSE' ? 'Gasto' : 'Ingreso'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Print-only view */}
      <div className="print-only">
        <div className="print-header">
          <h1>Reporte Mensual</h1>
          <h2>{formatMonth(currentDate)}</h2>
        </div>

        <div className="print-summary">
          <div className="print-summary-item">
            <span className="print-label">Total Ingresos</span>
            <span className="print-amount income">${totals.income.toFixed(2)}</span>
          </div>
          <div className="print-summary-item">
            <span className="print-label">Total Gastos</span>
            <span className="print-amount expense">${totals.expenses.toFixed(2)}</span>
          </div>
          <div className="print-summary-item">
            <span className="print-label">Balance</span>
            <span className={`print-amount ${balance >= 0 ? 'income' : 'expense'}`}>
              ${balance.toFixed(2)}
            </span>
          </div>
        </div>

        <table className="print-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Descripción</th>
              <th>Categoría</th>
              <th>Monto</th>
              <th>Usuario</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((transaction) => (
              <tr key={transaction.id}>
                <td>
                  {new Date(transaction.date).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </td>
                <td>{transaction.description || '-'}</td>
                <td>
                  {typeof transaction.category === 'string'
                    ? transaction.category
                    : transaction.category?.name || 'Sin categoría'}
                </td>
                <td className={transaction.type === 'EXPENSE' ? 'expense' : 'income'}>
                  {transaction.type === 'EXPENSE' ? '-' : ''}${transaction.amount.toFixed(2)}
                </td>
                <td>{transaction.userInitials || 'Usuario'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedTransaction && (
        <TransactionModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
};

export default Report; 